export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  isApproximate: boolean; // true if not ROOFTOP precision
}

interface GeocodableAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

interface ShipmentWithAddresses {
  shipFrom?: GeocodableAddress;
  shipTo?: GeocodableAddress;
  [key: string]: unknown;
}

export async function geocodeAddress(address: {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
}): Promise<GeocodeResult | null> {
  // Construct the address string
  const addressParts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.postalCode,
    address.country ?? "Canada",
  ].filter(Boolean);

  const addressString = addressParts.join(", ");

  try {
    // Use Google Maps Geocoding API
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      void geocoder.geocode({ address: addressString }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
          const result = results[0];
          // ROOFTOP = precise, anything else is approximate
          const locationType = result.geometry.location_type;
          const isApproximate =
            locationType !== google.maps.GeocoderLocationType.ROOFTOP;
          resolve({
            latitude: result.geometry.location.lat(),
            longitude: result.geometry.location.lng(),
            formattedAddress: result.formatted_address,
            isApproximate,
          });
        } else {
          console.warn("Geocoding failed for address:", addressString, status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error("Error geocoding address:", error);
    return null;
  }
}

export async function validateAndGeocodeAddresses(
  shipments: ShipmentWithAddresses[]
): Promise<ShipmentWithAddresses[]> {
  const geocodedShipments = await Promise.all(
    shipments.map(async (shipment) => {
      const updatedShipment = { ...shipment };

      // Geocode shipFrom address
      if (shipment.shipFrom) {
        const fromResult = await geocodeAddress(shipment.shipFrom);
        if (fromResult) {
          updatedShipment.shipFrom = {
            ...shipment.shipFrom,
            latitude: fromResult.latitude,
            longitude: fromResult.longitude,
          };
        }
      }

      // Geocode shipTo address
      if (shipment.shipTo) {
        const toResult = await geocodeAddress(shipment.shipTo);
        if (toResult) {
          updatedShipment.shipTo = {
            ...shipment.shipTo,
            latitude: toResult.latitude,
            longitude: toResult.longitude,
          };
        }
      }

      return updatedShipment;
    })
  );

  return geocodedShipments;
}
